import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TabComponent } from "@/components/tabs/TabComponent";
import { GrossMarginRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { DollarData } from "@/pages/config/price-board/schema/price-board.schema";
import { getDollarSection } from "@/services/price-board.service";
import {
  GrossMarginRequestDetail as GrossMarginDetailType,
} from "@/services/gross-margin-requests.service";
import { getGranosProductivos, GranoProductivo } from "@/services/productive-entities.service";
import {
  getZonalGrossMarginByCultivo,
  ZonalGrossMarginDetail,
} from "@/services/zonal-gross-margin.service";
import {
  Flex,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  ConfirmGrossMarginModal,
  CultivoConfirmData,
} from "./ConfirmGrossMarginModal";
import {
  GrossMarginCultivoTab,
} from "./GrossMarginCultivoTab";
import {
  fetchGrossMarginRequestDetailHandler,
  submitGrossMarginResultsHandler,
  tomarGrossMarginRequestHandler,
} from "./handler-functions";
import { InformeModal } from "./InformeModal";
import { MBPMainData } from "./components/MBPMainData";
import { MBDetailButtons } from "./components/MBDetailButtons";
import { buildCultivosDto, parseFormValue } from "../schema/helper-functions";

/**
 * Página de detalle de una Solicitud de Margen Bruto Personalizado.
 *
 * Ciclo de vida de la solicitud:
 *   Pendiente → [Tomar] → EnProceso → [Completar y enviar] → Confirmada
 *
 * Flujo de "Completar y enviar" (dos modales en cadena):
 *   1. InformeModal: el usuario adjunta el informe PDF/XLS y obtiene la URL del blob.
 *   2. ConfirmGrossMarginModal: revisión final antes de hacer PATCH al estado Confirmada.
 */

export const GrossMarginRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [detail, setDetail] = useState<GrossMarginDetailType | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingTomar, setLoadingTomar] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [latestDollarValue, setLatestDollarValue] = useState<number | null>(null);
  const [grains, setGrains] = useState<GranoProductivo[]>([]);
  const [zonalData, setZonalData] = useState<Record<number, ZonalGrossMarginDetail>>({});

  const [openInforme, setOpenInforme] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingInforme, setPendingInforme] = useState<{
    notas: string;
  } | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchDetail = useCallback(async () => {
    await fetchGrossMarginRequestDetailHandler({
      id,
      setLoading: setLoadingDetail,
      setDetail,
      navigate,
      fetchPermissions,
    });
  }, [id, navigate, fetchPermissions]);

  const fetchDetailSilent = useCallback(async () => {
    await fetchGrossMarginRequestDetailHandler({
      id,
      setLoading: () => {},
      setDetail,
      navigate,
      fetchPermissions,
    });
  }, [id, navigate, fetchPermissions]);

  useEffect(() => {
    if (!detail?.idZonaProductiva || !detail?.idCampProductiva || !detail?.cultivos?.length) return;
    Promise.all(
      detail.cultivos.map((c) =>
        getZonalGrossMarginByCultivo({
          IdGranoProductivo: c.granoProductivo.id,
          IdZonaProductiva: detail.idZonaProductiva,
          IdCampProd: detail.idCampProductiva,
        }).then((data) => ({ granoId: c.granoProductivo.id, data })),
      ),
    ).then((results) => {
      const map: Record<number, ZonalGrossMarginDetail> = {};
      results.forEach(({ granoId, data }) => {
        if (data) map[granoId] = data;
      });
      setZonalData(map);
    });
  }, [detail?.id]);

  const fetchLatestDollarQuotation = async (): Promise<number | null> => {
    const today = dayjs();
    const response = (await getDollarSection({
      fechaDesde: today.subtract(60, "day").format("YYYY-MM-DD"),
      fechaHasta: today.format("YYYY-MM-DD"),
    })) as DollarData | null;

    const latestDollar = [...(response?.dolarDivisa ?? [])]
      .sort((a, b) => dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf())[0];

    return latestDollar?.venta ?? null;
  };

  useEffect(() => {
    if (isActive && id) {
      fetchDetail();
      Promise.all([
        fetchLatestDollarQuotation(),
        getGranosProductivos({ AplicaPPMB: true }),
      ]).then(([dollarValue, grainsData]) => {
        if (dollarValue) setLatestDollarValue(dollarValue);
        setGrains(grainsData);
      });
    }
  }, [isActive, id]);

  const status = detail?.estado as GrossMarginRequestStatus | undefined;
  const isPending = status === GrossMarginRequestStatus.PENDING;
  const isEnProceso = status === GrossMarginRequestStatus.IN_PROCESS;
  const isConfirmed = status === GrossMarginRequestStatus.CONFIRMED;

  // ── form (EnProceso only) ──
  const { control, getValues, reset } = useForm<FieldValues>({
    defaultValues: { cultivos: [] },
  });

  const watchedCultivos = useWatch({ control, name: "cultivos" }) as any[];

  useEffect(() => {
    if (!detail?.cultivos?.length) return;
    reset({
      cultivos: detail.cultivos.map((c) => {
        const grain = grains.find((g) => g.id === c.granoProductivo.id);
        const precioPizarra = grain?.precioPizarra ?? 0;
        const zonal = zonalData[c.granoProductivo.id];
        return {
          cotizacionDolar: c.cotizacionDolar || (latestDollarValue ?? ""),
          precioCereal: c.precioCereal || (precioPizarra
            ? Number((precioPizarra / 10).toFixed(2))
            : ""),
          rindeEsperado: c.rindeEsperado || c.rindeIndicado || "",
          precioAgroquimicos: c.precioAgroquimicos || zonal?.precioAgroquimicos || "",
          precioFertilizantes: c.precioFertilizantes || zonal?.precioFertilizantes || "",
          precioSemillas: c.precioSemillas || zonal?.precioSemillas || "",
          precioLabores: c.precioLabores || zonal?.precioLabores || "",
          precioSeguros: c.precioSeguros || zonal?.precioSeguros || "",
          precioAlquiler: c.precioAlquiler || "",
          gastoDeCosecha: c.gastoDeCosecha || "",
        };
      }),
    });
  }, [detail, reset, latestDollarValue, grains, zonalData]);

  const isFormComplete = useMemo(() => {
    if (!isEnProceso || !watchedCultivos?.length) return false;
    //Si todos los cultivos de la solicitud tienen sus datos completados
    return watchedCultivos.every((c: any) =>
      [
        c?.cotizacionDolar,
        c?.precioCereal,
        c?.rindeEsperado,
        c?.precioAgroquimicos,
        c?.precioFertilizantes,
        c?.precioSemillas,
        c?.precioLabores,
        c?.precioSeguros,
        c?.gastoDeCosecha,
      ].every((v) => v !== "" && v !== undefined && v !== null && String(v).trim() !== ""),
    );
  }, [isEnProceso, watchedCultivos]);

  // ── handlers ──
  const handleBack = () => navigate(AppRoutes.GROSS_MARGIN_REQUESTS);

  const handleTomar = async () => {
    if (!detail?.id) return;
    await tomarGrossMarginRequestHandler({
      id: detail.id,
      setLoading: setLoadingTomar,
      onSuccess: fetchDetailSilent,
      navigate,
      fetchPermissions,
    });
  };

  const handleInformeAccept = (notas: string) => {
    setPendingInforme({ notas });
    setOpenInforme(false);
    setOpenConfirm(true);
  };


  const handleConfirmAccept = async () => {
    if (!detail?.id || !pendingInforme) return;
    await submitGrossMarginResultsHandler({
      id: detail.id,
      cultivos: buildCultivosDto({
        getValues,
        detail
      }),
      notasInforme: pendingInforme.notas,
      setLoading: setLoadingSubmit,
      onSuccess: async () => {
        setOpenConfirm(false);
        setPendingInforme(null);
        navigate(AppRoutes.GROSS_MARGIN_REQUESTS);
      },
      navigate,
      fetchPermissions,
    });
  };

  // ── confirm data (used by modal) ──
  const cultivosConfirmData: CultivoConfirmData[] = useMemo(() => {
    if (!detail?.cultivos?.length) return [];
    const formCultivos = (watchedCultivos ?? []) as any[];
    return detail.cultivos.map((c, i) => ({
      granoNombre: c.granoProductivo.nombre,
      cotizacionDolar: parseFormValue(formCultivos[i]?.cotizacionDolar),
      precioCereal: parseFormValue(formCultivos[i]?.precioCereal),
      rindeEsperado: parseFormValue(formCultivos[i]?.rindeEsperado),
      precioAgroquimicos: parseFormValue(formCultivos[i]?.precioAgroquimicos),
      precioFertilizantes: parseFormValue(formCultivos[i]?.precioFertilizantes),
      precioSemillas: parseFormValue(formCultivos[i]?.precioSemillas),
      precioLabores: parseFormValue(formCultivos[i]?.precioLabores),
      precioSeguros: parseFormValue(formCultivos[i]?.precioSeguros),
      precioAlquiler: parseFormValue(formCultivos[i]?.precioAlquiler),
      gastoDeCosecha: parseFormValue(formCultivos[i]?.gastoDeCosecha),
    }));
  }, [detail, watchedCultivos]);

  // ── tabs (EnProceso / Confirmada) ──
  const tabs = useMemo(() => {
    const informationTab = {
      value: "informacion",
      label: "Información",
      content: <MBPMainData detail={detail} />,
    };

    if ((!isEnProceso && !isConfirmed) || !detail?.cultivos?.length) return [informationTab];

    const cultivoTabs = detail.cultivos.map((c, i) => ({
      value: `cultivo-${i}`,
      label: `Carga ${c.granoProductivo.nombre}`,
      labelClassName: "account-value-label",
      content: (
        <GrossMarginCultivoTab
          index={i}
          control={control}
          granoNombre={c.granoProductivo.nombre}
          establecimiento={detail.procedencia}
          tipoCampo={detail.tipoCampo}
          rindeIndicado={c.rindeIndicado}
          disabled={isConfirmed || loadingSubmit}
        />
      ),
    }));

    return [informationTab, ...cultivoTabs];
  }, [detail, isEnProceso, isConfirmed, control, loadingSubmit]);

  // ── render ──
  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px" height="100%" minHeight={0}>
      <SectionHeader
        title="Margen Bruto Personalizado"
        loading={loadingDetail}
        aditionalButtons={
         <MBDetailButtons 
            isEnProceso={isEnProceso}
            isFormComplete={isFormComplete}
            isPending={isPending}
            loadingSubmit={loadingSubmit}
            loadingTomar={loadingTomar}
            setOpenInforme={setOpenInforme}
            handleTomar={handleTomar}
            handleBack={handleBack}
         />
        }
      />

      {!loadingDetail && detail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="16px"
          paddingRight="8px"
          paddingBottom="6px"
        >
          {isEnProceso || isConfirmed ? (
            <TabComponent tabs={tabs} defaultValue="informacion" />
          ) : (
            <MBPMainData detail={detail} />
          )}
        </Flex>
      )}

      <InformeModal
        open={openInforme}
        setOpen={setOpenInforme}
        idSolicitud={detail?.id ?? 0}
        onAccept={handleInformeAccept}
      />

      <ConfirmGrossMarginModal
        open={openConfirm}
        setOpen={setOpenConfirm}
        cultivosData={cultivosConfirmData}
        onAccept={handleConfirmAccept}
        onCancel={() => setOpenInforme(true)}
        loading={loadingSubmit}
      />
    </Flex>
  );
};


